import { includes } from "lodash";

import { getPolicy } from "../promises/get-policy";
import { getContextWithWrapper, getProduct } from "../promises/quotes";
import IAT_CARRIER_GROUP_IDS from "../static-data/iat-carrier-group-ids";
import {
  LOADED,
  LOADING,
  LOAD_ERROR,
  PRELOAD
} from "../static-data/loading-state-enum";
import * as singlePromiseCache from "../utils/single-promise-cache";

import { pickEnvInfo, selectAll, selectAuthString } from "./selectors";
import {
  parseBilling,
  parseDocuments,
  parseHeaderSections,
  parseNotes,
  parsePolicyActivity
} from "./utils/policy-view";
import executeContext from "./utils/quote-view/execute-context";
import { flattenQuoteData } from "./utils/flatten-quote-data";

const MAKE_REQUEST = "policy-view/MAKE_REQUEST";
const GET_RESPONSE = "policy-view/GET_RESPONSE";
const ERROR = "policy-view/ERROR";
const SEARCH_TEXT = "policy-view/SEARCH_TEXT";
const POLICYHOLDER_INFO_CHANGE = "policy-view/POLICYHOLDER_INFO_CHANGE";
const MORTGAGEE_CHANGE = "policy-view/MORTGAGEE_CHANGE";

export const makeRequest = quoteNumber => ({
  type: MAKE_REQUEST,
  quoteNumber
});

export const getResponse = ({ quoteNumber, data }) => ({
  type: GET_RESPONSE,
  quoteNumber,
  data
});

const setError = error => ({
  type: ERROR,
  error
});

export const loadPolicy = quoteNumber => async (dispatch, getState) => {
  const [{ username: currentUsername }, auth] = selectAll(getState(), [
    pickEnvInfo,
    selectAuthString
  ]);
  dispatch(makeRequest(quoteNumber));
  const getPolicyUsing = {
    id: quoteNumber,
    auth
  };
  const getPolicyPromise = getPolicy(getPolicyUsing);
  singlePromiseCache.store({
    promise: getPolicyPromise,
    request: getPolicy,
    using: getPolicyUsing
  });
  return getPolicyPromise
    .then(policy => {
      return Promise.all([
        policy,
        getContextWithWrapper({ id: policy?.identifiers?.QuoteNumber }),
        getProduct({ id: null, policyId: policy?.identifiers?.PolicyID }).catch(
          error => {
            console.error(error);
            return {};
          }
        )
      ]);
    })
    .then(([policy, context, product]) => {
      const { CarrierGroupID } = policy.policyData;
      const isIAT = includes(IAT_CARRIER_GROUP_IDS, CarrierGroupID);
      const { rawPolicyXML, documents, ...restPolicy } = policy;
      dispatch(
        getResponse({
          quoteNumber,
          data: {
            quoteId: quoteNumber,
            policyId: policy.identifiers.PolicyID,
            ...restPolicy,
            insuredName: policy.policyData.InsuredName,
            productType: policy.policyData.PolicyType,
            carrier: policy.management.CarrierId,
            effectiveDate: policy.policyData.EffectiveDate,
            expirationDate: policy.policyData.ExpirationDate,
            sections: executeContext(
              context,
              flattenQuoteData({
                effectiveDate: policy.policyData.EffectiveDate,
                inputs: policy.policyData
              })
            ),
            billing: parseBilling(policy),
            notes: parseNotes(policy.relatedItems, currentUsername),
            // this would get overwritten by our ...restPolicy below if we didn't
            // deconstruct documents and pass it in here
            documents: parseDocuments(documents),
            headerSections: parseHeaderSections(policy),
            policyActivity: parsePolicyActivity(
              policy.eventHistory,
              policy.revisionHistory
            ),
            claimsInfo: {
              phone: policy.policyData.CarrierClaimPhoneNumber,
              carrierName: policy.policyData.CarrierName,
              carrierGroupId: policy.policyData.CarrierGroupID,
              carrierId: policy.policyData.CarrierID,
              isIAT
            },
            product,
            rawPolicyXML
          }
        })
      );
    })
    .catch(error => {
      console.error(error);
      dispatch(setError(error));
    });
};

export const refreshPolicy = () => (dispatch, getState) => {
  const { quoteNumber } = getState().policyView;
  dispatch(loadPolicy(quoteNumber));
};

export const setSearchText = searchText => ({
  type: SEARCH_TEXT,
  searchText
});

export const showPolicyholderInfoChange = (show = true) => ({
  type: POLICYHOLDER_INFO_CHANGE,
  show
});

export const showMortgageeChange = (show = true) => ({
  type: MORTGAGEE_CHANGE,
  show
});

export const policyView = (
  state = {
    quoteNumber: null,
    loadingState: PRELOAD,
    data: null,
    error: undefined,
    showPolicyholderInfoChangeModal: false,
    showMortgageeChangeModal: false,
    searchText: ""
  },
  data
) => {
  switch (data.type) {
    case MAKE_REQUEST: {
      return {
        ...state,
        loadingState: LOADING,
        quoteNumber: data.quoteNumber,
        data: state.quoteNumber === data.quoteNumber ? state.data : null,
        searchText: ""
      };
    }
    case GET_RESPONSE: {
      return state.quoteNumber !== data.quoteNumber
        ? state
        : {
            ...state,
            data: data.data,
            loadingState: LOADED,
            error: undefined
          };
    }
    case ERROR: {
      return {
        ...state,
        error: data.error,
        loadingState: LOAD_ERROR,
        data: null
      };
    }
    case SEARCH_TEXT: {
      return {
        ...state,
        searchText: data.searchText
      };
    }
    case POLICYHOLDER_INFO_CHANGE: {
      return {
        ...state,
        showPolicyholderInfoChangeModal: data.show
      };
    }
    case MORTGAGEE_CHANGE: {
      return {
        ...state,
        showMortgageeChangeModal: data.show
      };
    }

    default:
      return state;
  }
};
